// Track-attach helpers shared between MeetParticipantTile (first-render
// best-effort attach) and connectCall (per-LiveKit-event attach). Lives
// under functions/ so a frontend component file doesn't have to reach
// across into another component's source — both consumers import from
// here.
//
// DOMQL v3.14's `onRender` doesn't reliably refire on root-state mutations
// (livekitParticipants), so the tile-side attach alone misses every
// remote track that subscribes after first mount AND every local track
// publish (setMicrophoneEnabled / setCameraEnabled run AFTER initial
// _refreshParticipants). The Room-event-driven path in connectCall.js
// covers those — both call `attachParticipantTracks(room, identity, isLocal)`,
// which finds the tile by `data-identity` and attaches if the track
// changed.

// Iterate publications across livekit-client versions (Map or object).
const _iterPubs = (obj) => {
  if (!obj) return []
  if (typeof obj.values === 'function') return [...obj.values()]
  return Object.values(obj)
}

// Resolve the video publication to attach. `mediaSource` selects between
// the participant's camera track and their screen-share track — the same
// participant can publish both at once (the connectCall.js participant
// list emits a separate virtual tile for the screen track). Falls back
// to whichever pub is present when the preferred source isn't published.
const _pickVideoPub = (participant, mediaSource) => {
  const pubs = _iterPubs(participant.videoTrackPublications)
  const wantsScreen = mediaSource === 'screen_share'
  const isScreen = (p) => p.source === 'screen_share' || p.source === 'screenShare'
  const isCamera = (p) => p.source === 'camera' || (!isScreen(p) && p.kind === 'video')
  // First pass: exact match on requested source.
  for (const pub of pubs) {
    if (!pub.track) continue
    if (wantsScreen && isScreen(pub)) return pub
    if (!wantsScreen && isCamera(pub)) return pub
  }
  // Fallback: ANY video pub — better to attach something than nothing.
  for (const pub of pubs) if (pub.track && pub.kind === 'video') return pub
  return null
}

const _attachVideoTrack = (videoEl, room, identity, isLocal, mediaSource) => {
  if (!videoEl || !room) return
  const participant = isLocal
    ? room.localParticipant
    : (typeof room.remoteParticipants?.get === 'function'
        ? room.remoteParticipants.get(identity)
        : room.remoteParticipants?.[identity])
  if (!participant) return

  const targetPub = _pickVideoPub(participant, mediaSource)

  const currentSid = videoEl._lkTrackSid || null
  const targetSid = targetPub?.trackSid || null
  if (currentSid === targetSid) return

  if (videoEl._lkTrack) {
    try { videoEl._lkTrack.detach(videoEl) } catch (err) { console.warn('[meet/attachTracks] video detach failed', err) }
  }
  if (targetPub?.track) {
    try {
      targetPub.track.attach(videoEl)
      videoEl._lkTrack = targetPub.track
      videoEl._lkTrackSid = targetSid
    } catch (err) { console.warn('[meet] video attach failed', err) }
  } else {
    videoEl._lkTrack = null
    videoEl._lkTrackSid = null
  }
}

const _attachAudioTrack = (audioEl, room, identity, isLocal) => {
  if (!audioEl || !room || isLocal) return
  const participant = typeof room.remoteParticipants?.get === 'function'
    ? room.remoteParticipants.get(identity)
    : room.remoteParticipants?.[identity]
  if (!participant) return

  let targetPub = null
  for (const pub of _iterPubs(participant.audioTrackPublications)) {
    if (pub.track) { targetPub = pub; break }
  }

  const currentSid = audioEl._lkTrackSid || null
  const targetSid = targetPub?.trackSid || null
  if (currentSid === targetSid) return

  if (audioEl._lkTrack) {
    try { audioEl._lkTrack.detach(audioEl) } catch (err) { console.warn('[meet/attachTracks] audio detach failed', err) }
  }
  if (targetPub?.track) {
    try {
      targetPub.track.attach(audioEl)
      audioEl._lkTrack = targetPub.track
      audioEl._lkTrackSid = targetSid
    } catch (err) { console.warn('[meet] audio attach failed', err) }
  } else {
    audioEl._lkTrack = null
    audioEl._lkTrackSid = null
  }
}

// Find a tile by its `data-tile-id` (set on MeetParticipantTile via
// `attr:`) — the tile id is `<identity>` for the camera tile and
// `<identity>:screen` for the virtual screen-share tile, matching the
// `id` field connectCall.js emits in `livekitParticipants`. Attaches
// the correct video pub based on `mediaSource`. Idempotent.
export const attachParticipantTracks = (room, identity, isLocal, mediaSource = 'camera') => {
  if (!room || !identity) return
  const tileId = mediaSource === 'screen_share' ? `${identity}:screen` : identity
  const safe = String(tileId).replace(/"/g, '\\"')
  const tile = document.querySelector(`[data-tile-id="${safe}"]`)
  const videoEl = tile ? tile.querySelector('video') : null
  const tileAudio = tile ? tile.querySelector('audio') : null
  if (videoEl) _attachVideoTrack(videoEl, room, identity, isLocal, mediaSource)
  // Audio plays from ONE place, for the camera only (never the screen-share
  // tile — the same mic twice) and never the local one. The shell's
  // call-audio host (components/CallAudio.js) outlives /meet, so a live call
  // keeps its sound on every route — the tiles unmount with /meet, and a call
  // went silent behind the dock. A tile's own <audio> plays only where no
  // host is mounted, and never beside it.
  if (mediaSource === 'screen_share' || isLocal) return
  const who = String(identity).replace(/"/g, '\\"')
  const hostAudio = document.querySelector(`[data-meet-audio-for="${who}"]`)
  if (hostAudio) {
    if (tileAudio && tileAudio._lkTrack) {
      try {
        tileAudio._lkTrack.detach(tileAudio)
      } catch (err) {
        console.warn('[meet/attachTracks] tile audio detach failed', err)
      }
      tileAudio._lkTrack = null
      tileAudio._lkTrackSid = null
    }
    _attachAudioTrack(hostAudio, room, identity, false)
  } else if (tileAudio) {
    _attachAudioTrack(tileAudio, room, identity, false)
  }
}

// The call-audio host's own element (components/CallAudio.js): its remote
// member's audio track. Idempotent — the same track is never attached twice.
export const attachParticipantAudio = (audioEl, room, identity) => {
  if (!audioEl || !room || !identity) return
  _attachAudioTrack(audioEl, room, identity, false)
}

// Sweep: attach for every participant + virtual screen-share tile
// currently in the room. Used after connect() resolves and whenever the
// participant list refreshes — any tracks already published get picked up.
export const attachAllParticipantTracks = (room) => {
  if (!room) return
  const local = room.localParticipant
  if (local) {
    attachParticipantTracks(room, local.identity, true, 'camera')
    // Local screen-share — if the local user is sharing, attach it to
    // its dedicated tile too. The local tile is muted by the tile
    // component's `muted: 'muted'` attr to avoid echo, so this is just
    // a visible mirror of what's being broadcast.
    attachParticipantTracks(room, local.identity, true, 'screen_share')
  }
  const remoteValues = typeof room.remoteParticipants?.values === 'function'
    ? room.remoteParticipants.values()
    : Object.values(room.remoteParticipants || {})
  for (const p of remoteValues) {
    if (typeof p.identity === 'string' && p.identity.startsWith('agent-')) continue
    attachParticipantTracks(room, p.identity, false, 'camera')
    attachParticipantTracks(room, p.identity, false, 'screen_share')
  }
}

// ── Featured/stage video attach ─────────────────────────────────────
// Moved from components/FeaturedTile.js (2026-06-12): the stage video
// only attached in the component's onRender/onUpdate, which v3.14 does
// NOT fire reliably on root-state changes — when someone started a
// screen-share the stage un-hid but stayed BLACK ('why I dont see when
// someone is sharing screen'). Now ALSO called event-side from
// applyTileGridLayout on every participant/speaker event.
const _iterVideoPubs = (participant) => {
  const pubs = participant.videoTrackPublications
  if (!pubs) return []
  if (typeof pubs.values === 'function') return [...pubs.values()]
  return Object.values(pubs)
}

// Attach the featured participant's video track to the featured <video> node.
// Idempotent: compares trackSid, skips if unchanged.
export const attachFeaturedVideo = (videoNode, featured, livekitRoom) => {
  if (!videoNode || !featured || !livekitRoom) return

  const isLocal = featured.isLocal
  const identity = featured.identity
  const mediaSource = featured.mediaSource || (featured.isScreen ? 'screen_share' : 'camera')

  const participant = isLocal
    ? livekitRoom.localParticipant
    : (typeof livekitRoom.remoteParticipants?.get === 'function'
        ? livekitRoom.remoteParticipants.get(identity)
        : livekitRoom.remoteParticipants?.[identity])

  if (!participant) return

  // Resolve the correct publication.
  const wantsScreen = mediaSource === 'screen_share'
  const isScreenPub = (p) => p.source === 'screen_share' || p.source === 'screenShare'
  const isCameraPub = (p) => p.source === 'camera' || (!isScreenPub(p) && p.kind === 'video')

  let targetPub = null
  const pubs = _iterVideoPubs(participant)
  for (const pub of pubs) {
    if (!pub.track) continue
    if (wantsScreen && isScreenPub(pub)) { targetPub = pub; break }
    if (!wantsScreen && isCameraPub(pub)) { targetPub = pub; break }
  }
  if (!targetPub) {
    for (const pub of pubs) {
      if (pub.track && pub.kind === 'video') { targetPub = pub; break }
    }
  }

  const currentSid = videoNode._lkFeaturedSid || null
  const targetSid = targetPub?.trackSid || null
  if (currentSid === targetSid) return

  if (videoNode._lkFeaturedTrack) {
    try { videoNode._lkFeaturedTrack.detach(videoNode) } catch (err) { console.warn('[meet/FeaturedTile] video detach failed', err) }
  }
  if (targetPub?.track) {
    try {
      targetPub.track.attach(videoNode)
      videoNode._lkFeaturedTrack = targetPub.track
      videoNode._lkFeaturedSid = targetSid
    } catch (err) {
      console.warn('[meet/attachTracks] featured video attach failed', err)
    }
  } else {
    videoNode._lkFeaturedTrack = null
    videoNode._lkFeaturedSid = null
  }
}
